import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { temperatureContext } from '../../../lib/context';
import { dataUtils, hasHumidityValue } from '../composables/utils';

// showHumidity defaults on so a caller that predates the flag keeps the column.
function TemperatureTable({ detail, record, showHumidity = true }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime' });
    const tempUnit = useContext(temperatureContext);
    const unit = tempUnit === 'fahrenheit' ? '°F' : '°C';

    // Newest readings first.
    const rows = detail.slice().reverse();

    return (
        <div className='w-full'>
            <table className='w-full border-collapse text-sm'>
                <thead>
                    <tr className='text-left font-semibold text-slate-600'>
                        <th className='sticky top-0 z-10 bg-[#E0EDFB] px-4 py-3'>{t('time')}</th>
                        <th className='sticky top-0 z-10 bg-[#E0EDFB] px-4 py-3'>{t('temperature')}</th>
                        {/* Humidity shares this table rather than getting its own: the
                            readings arrive on the same timestamps (issue #203). */}
                        {showHumidity && <th className='sticky top-0 z-10 bg-[#E0EDFB] px-4 py-3'>{t('humidity')}</th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row: any) => {
                        const hasReading =
                            row.temperature !== null && row.temperature !== undefined && row.temperature !== '';
                        // Judged against the thresholds that applied at the row's own
                        // timestamp, so a working-hours device doesn't colour its
                        // off-shift readings against the working limits.
                        const isNormal = record ? dataUtils.isReadingNormal(record, row) : true;
                        const hasHumidity = hasHumidityValue(row.humidity);
                        const isHumidityNormal = record ? dataUtils.isHumidityReadingNormal(record, row) : true;
                        return (
                            <tr key={row.time} className='border-b border-slate-100'>
                                <td className='px-4 py-3 text-slate-600'>{row.time}</td>
                                <td className='px-4 py-3'>
                                    <span
                                        className={`inline-flex w-16 items-center justify-center rounded-full px-3 py-1 text-xs font-medium ${
                                            !hasReading
                                                ? 'bg-slate-100 text-slate-400'
                                                : isNormal
                                                  ? 'bg-[#EAF3FC] text-[#3F8EE8]'
                                                  : 'bg-[#FDECEC] text-[#FF5A5A]'
                                        }`}
                                    >
                                        {hasReading ? `${row.temperature}${unit}` : '--'}
                                    </span>
                                </td>
                                {showHumidity && (
                                    <td className='px-4 py-3'>
                                        <span
                                            className={`inline-flex w-16 items-center justify-center rounded-full px-3 py-1 text-xs font-medium ${
                                                !hasHumidity
                                                    ? 'bg-slate-100 text-slate-400'
                                                    : isHumidityNormal
                                                      ? 'bg-[#EAF3FC] text-[#3F8EE8]'
                                                      : 'bg-[#FDECEC] text-[#FF5A5A]'
                                            }`}
                                        >
                                            {hasHumidity ? `${row.humidity}%` : '--'}
                                        </span>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={showHumidity ? 3 : 2} className='px-4 py-10 text-center text-slate-400'>
                                {t('temperatureRecords')}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default TemperatureTable;
